import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react'
import { VideoCamera } from '@components/icons'
import { useDailyEvent, useDevices } from '@daily-co/daily-react'
import { useQueryParams } from '@hooks'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BlueNavLogo } from '../../images/brand/blue-nav-logo'
import { ClientActiveCall } from './components/telehealth/client-active-call'
import { ClientReadyToJoin } from './components/telehealth/client-ready-to-join'
import { ClientWaitingRoom } from './components/telehealth/client-waiting-room'
import { ClientWelcome } from './components/telehealth/client-welcome'
import { ClientTelehealthFooter } from './components/telehealth/components'
import { ActiveTelehealthSessionProvider } from './components/telehealth/context/active-telehealth-session.context'
import {
  PatientDailyManagerProvider,
  usePatientDailyManager
} from './components/telehealth/context/patient-daily-manager.context'
import { usePlaySound } from './components/telehealth/hooks/use-play-sound'

type PublicTelehealthSessionParams = {
  clinicianDisplayId: string
  patientCode: string
}

export const PublicTelehealthSession = () => {
  const { clinicianDisplayId, patientCode } =
    useParams<PublicTelehealthSessionParams>()

  const queryParams = useQueryParams()

  const devices = useDevices()
  const { playSound, sounds } = usePlaySound()
  const playParticipantJoinedSound = () => {
    playSound(
      sounds.NEW_PARTICIPANT,
      devices.currentSpeaker?.device.deviceId || 'default'
    )
  }

  useDailyEvent('participant-joined', event => {
    playParticipantJoinedSound()
  })

  const email = queryParams.get('email') || ''

  const {
    isInCall,
    isCallEnded,
    hasLeftCall,
    participantFullName,
    isReadyToJoin
  } = usePatientDailyManager()

  const content = () => {
    if (isCallEnded || hasLeftCall || !participantFullName) {
      return (
        <Flex
          direction="column"
          h="100%"
          w="100%"
          justifyContent="space-between"
          alignItems="center"
          p="small"
        >
          <Box
            h="46px"
            pl={{
              base: '0px',
              md: 'small'
            }}
            mt="xsmall"
            alignSelf={{
              base: 'center',
              md: 'flex-start'
            }}
          >
            <BlueNavLogo />
          </Box>
          <Flex
            mx="auto"
            direction="column"
            alignItems="center"
            p="medium"
            bg="white"
            borderRadius="16px"
            maxW="512px"
            w="100%"
          >
            {!participantFullName ? (
              <ClientWelcome />
            ) : (
              <>
                <VStack spacing={5}>
                  <Flex
                    w="48px"
                    h="48px"
                    bg="primary"
                    borderRadius="50%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <VideoCamera fill="white" width="24px" />
                  </Flex>
                  <Text fontWeight="bold" fontSize="24px">
                    {isCallEnded
                      ? 'This session has ended'
                      : 'You left the call'}
                  </Text>
                </VStack>
                {isCallEnded ? (
                  <Text mt="xsmall">You can close this window.</Text>
                ) : (
                  <Button
                    size="lg"
                    mt="small"
                    onClick={() => window.location.reload()}
                  >
                    Return to call
                  </Button>
                )}
              </>
            )}
          </Flex>
          <Box h="46px" />
        </Flex>
      )
    }

    if (isInCall) {
      return (
        <Box w="100%" h="100%" pb="60px" bg="black">
          <ClientActiveCall />
          <ClientTelehealthFooter />
        </Box>
      )
    }

    if (isReadyToJoin) {
      return (
        <Flex
          w="100%"
          h="100%"
          p="8px"
          pb="60px"
          bg="black"
          justify="center"
          align="center"
          position="relative"
        >
          <ClientReadyToJoin
            clinicianDisplayId={clinicianDisplayId}
            patientCode={patientCode}
            email={email}
          />
          <ClientTelehealthFooter />
        </Flex>
      )
    }

    return <ClientWaitingRoom />
  }

  return (
    <Box h="100%" w="100%" bgColor="#F6F7FE">
      <Flex
        h="100%"
        w="100%"
        overflowY="auto"
        align={{
          base: 'flex-start',
          md: 'center'
        }}
      >
        {content()}
      </Flex>
    </Box>
  )
}

const PublicTelehealthSessionWithContext = () => {
  const { isBackgroundBlurred, toggleBackgroundBlur } = usePatientDailyManager()
  const { clinicianDisplayId } = useParams<PublicTelehealthSessionParams>()

  return (
    <ActiveTelehealthSessionProvider
      defaultSidebarTab="none"
      isCallOwner={false}
      isBackgroundBlurred={isBackgroundBlurred}
      toggleBackgroundBlur={toggleBackgroundBlur}
      clinicianDisplayId={clinicianDisplayId}
    >
      <PublicTelehealthSession />
    </ActiveTelehealthSessionProvider>
  )
}

export const PublicTelehealthSessionWrapper = () => {
  return (
    <PatientDailyManagerProvider>
      <PublicTelehealthSessionWithContext />
    </PatientDailyManagerProvider>
  )
}
